import { useState } from "react";
import { ListPager } from "@/components/data-grid/ListPager";
import { usePagedRows } from "@/hooks/usePagedRows";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AirtableGrid, Column, GridToolbar } from "@/components/data-grid";
import { Plus, Trash2, ChevronRight, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddCompanyDrawer } from "@/components/add/AddCompanyDrawer";
import { ListShell } from "@/components/list/ListShell";
import { NameCell } from "@/components/list/NameCell";
import { ListTiles } from "@/components/list/ListTiles";
import { formatDealValue } from "@shared/formatDealValue";
import { ChartChips, ALL_KEY } from "@/components/list/ChartChips";
import { capitalProviderTiles } from "@/components/list/tiles/capitalProviderTiles";

// ─── Constants ───────────────────────────────────────

const CAPITAL_TYPES = [
  "Senior Debt", "Mezzanine", "Unitranche", "ABL", "SBA",
  "Equity Co-Invest", "Preferred Equity", "Seller Note",
  "Insurance", "BDC", "Other",
];

const CAPITAL_TYPE_COLORS: Record<string, string> = {
  "Senior Debt": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "Mezzanine": "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  "Unitranche": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
  "ABL": "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
  "SBA": "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  "Equity Co-Invest": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "Preferred Equity": "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  "Seller Note": "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  "Insurance": "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400",
  "BDC": "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  "Other": "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
};

interface CapitalProvider {
  id: string;
  name: string;
  category?: string | null;
  industry: string | null;
  headquarters: string | null;
  website: string | null;
  description: string | null;
  createdAt: string;
  engaged?: boolean;
  dualRoleBuyerCp?: boolean | null;
  contactCount: number;
  lastContactName: string | null;
  capitalProfile: {
    capitalTypes: string[] | null;
    minDealSize: string | null;
    maxDealSize: string | null;
    sweetSpot: string | null;
    industryFocus: string[] | null;
    geographicFocus: string | null;
  } | null;
}

// ─── Helpers ─────────────────────────────────────────

function formatDealRange(profile: CapitalProvider["capitalProfile"]): string | null {
  if (!profile) return null;
  const min = profile.minDealSize ? formatDealValue(profile.minDealSize) : null;
  const max = profile.maxDealSize ? formatDealValue(profile.maxDealSize) : null;
  if (min && max) return min === max ? min : `${min} to ${max}`;
  if (min) return `${min}+`;
  if (max) return `up to ${max}`;
  return profile.sweetSpot ? formatDealValue(profile.sweetSpot) : null;
}

// ─── Main Component ──────────────────────────────────

export default function CapitalProviders() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const { data: providers = [], isLoading } = useQuery<CapitalProvider[]>({
    queryKey: ["/api/capital-providers"],
    queryFn: async () => {
      const r = await fetch("/api/capital-providers", { credentials: "include" });
      return r.ok ? r.json() : [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/companies/${id}`, {
        method: "DELETE", credentials: "include",
      });
      if (!r.ok) throw new Error("Failed to delete");
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/capital-providers"] });
      toast.success("Capital provider deleted");
    },
    onError: () => {
      toast.error("Failed to delete capital provider");
    },
  });

  // Tiles and chip counts follow the search but not the capital-type filter (spec R3).
  const searched = providers.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()));
  const filtered = searched.filter((p) => !typeFilter || (p.capitalProfile?.capitalTypes || []).includes(typeFilter));
  // Per-type counts within the search, never within the type filter itself.
  const typeCounts: Record<string, number> = {};
  for (const p of searched) for (const t of p.capitalProfile?.capitalTypes || []) typeCounts[t] = (typeCounts[t] || 0) + 1;
  const paged = usePagedRows(filtered);

  const chipTypes = [
    ...CAPITAL_TYPES,
    ...Object.keys(typeCounts).filter((t) => !(CAPITAL_TYPES as readonly string[]).includes(t)).sort(),
  ];
  const dim = <span className="text-muted-foreground/50">—</span>;

  const columns: Column<CapitalProvider>[] = [
    {
      id: "name", header: "Name", width: "w-[260px]",
      cell: (p) => (
        <NameCell
          name={p.name}
          nameClassName="font-medium"
          after={p.category === "Buyer Network" || p.dualRoleBuyerCp ? (
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0 rounded">Also Buyer</span>
          ) : undefined}
          sub={p.headquarters || undefined}
          title={p.headquarters ? `${p.name} · ${p.headquarters}` : p.name}
        />
      ),
    },
    {
      id: "types", header: "Capital types", width: "w-[200px]",
      cell: (p) => {
        const types = p.capitalProfile?.capitalTypes || [];
        if (types.length === 0) return dim;
        return (
          <span className="flex items-center gap-1 min-w-0" title={types.join(", ")}>
            {types.slice(0, 2).map((type) => (
              <Badge key={type} variant="secondary" className={`text-[11px] px-1.5 py-0 shrink-0 ${CAPITAL_TYPE_COLORS[type] || CAPITAL_TYPE_COLORS.Other}`}>{type}</Badge>
            ))}
            {types.length > 2 && <span className="text-xs text-muted-foreground shrink-0">{`+${types.length - 2}`}</span>}
          </span>
        );
      },
    },
    {
      id: "range", header: "Deal range", width: "w-[140px]",
      cell: (p) => {
        const r = formatDealRange(p.capitalProfile);
        return r ? <span title={r} className="text-muted-foreground">{r}</span> : dim;
      },
    },
    {
      id: "industries", header: "Industries",
      cell: (p) => {
        const inds = p.capitalProfile?.industryFocus || [];
        if (inds.length === 0) return dim;
        const text = inds.slice(0, 2).join(", ") + (inds.length > 2 ? ` +${inds.length - 2}` : "");
        return <span title={inds.join(", ")} className="text-xs text-muted-foreground">{text}</span>;
      },
    },
    {
      id: "geography", header: "Geography", width: "w-[140px]",
      cell: (p) => {
        const g = p.capitalProfile?.geographicFocus;
        return g ? <span title={g} className="text-xs text-muted-foreground">{g}</span> : dim;
      },
    },
    {
      id: "contacts", header: "Contacts", width: "w-[80px]", className: "text-center", headerClassName: "text-center",
      cell: (p) => p.contactCount > 0
        ? <span title={p.lastContactName || undefined} className="text-xs text-muted-foreground">{p.contactCount}</span>
        : dim,
    },
    {
      id: "engaged", header: "Spoken to", width: "w-[80px]", className: "text-center", headerClassName: "text-center",
      cell: (p) => (p.engaged
        ? <CheckCircle2 className="inline w-4 h-4 text-emerald-600 dark:text-emerald-400" aria-label="Spoken to" />
        : dim),
    },
  ];

  const rowActions = (p: CapitalProvider) => [
    { label: "View Details", icon: <ChevronRight className="w-4 h-4" />, onClick: () => setLocation(`/capital-providers/${p.id}`) },
    ...(p.website ? [{
      label: "Visit Website", icon: <Globe className="w-4 h-4" />,
      onClick: () => window.open(p.website!.startsWith("http") ? p.website! : `https://${p.website}`, "_blank", "noopener"),
    }] : []),
    { label: "Delete", icon: <Trash2 className="w-4 h-4" />, variant: "destructive" as const, onClick: () => setPendingDelete({ id: p.id, name: p.name }) },
  ];

  const typeChips = (
    <ChartChips
      ariaLabel="Capital type"
      chips={chipTypes.map((t) => ({ key: t, label: t, count: typeCounts[t] ?? 0 }))}
      allCount={searched.length}
      value={typeFilter ?? ALL_KEY}
      onChange={(k) => setTypeFilter(k === ALL_KEY ? null : k)}
    />
  );

  return (
    <ListShell
      title="Capital Providers"
      tiles={<ListTiles tiles={capitalProviderTiles(searched)} />}
      toolbar={<GridToolbar density="compact" searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search capital providers..."
        chips={typeChips}
        primaryAction={<Button size="sm" className="h-8 gap-1.5" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add Provider</Button>} />}
      footer={<ListPager density="compact" shown={paged.rows.length} total={paged.total} noun={["capital provider", "capital providers"]}
        page={paged.page} totalPages={paged.totalPages} pageSize={paged.pageSize} onPageSizeChange={paged.setPageSize}
        hasPrev={paged.hasPrev} hasNext={paged.hasNext} onPrev={paged.prev} onNext={paged.next} testIdPrefix="capital-providers" />}
      after={<>
        <AddCompanyDrawer open={addOpen} onOpenChange={setAddOpen} preselectedCategory="capital-providers" />
        <ConfirmDialog
          open={!!pendingDelete}
          onOpenChange={(next) => !next && !deleteMutation.isPending && setPendingDelete(null)}
          title={pendingDelete ? `Delete ${pendingDelete.name}?` : ""}
          description="This archives the capital provider so it no longer shows in the active list. You can restore it anytime from the Archived page in Settings."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="destructive"
          isLoading={deleteMutation.isPending}
          onConfirm={async () => {
            if (!pendingDelete) return;
            await deleteMutation.mutateAsync(pendingDelete.id);
            setPendingDelete(null);
          }}
        />
      </>}
    >
      <AirtableGrid density="compact" scroll data={paged.rows} columns={columns} getRowId={(p) => p.id}
        onRowClick={(p) => setLocation(`/capital-providers/${p.id}`)} getRowActions={rowActions} loading={isLoading}
        emptyState={<div className="p-8 text-center text-sm text-muted-foreground">
          {providers.length === 0 ? "No capital providers yet. Add your first one with Add Provider." : "No capital providers match your filters."}
        </div>} />
    </ListShell>
  );
}
